import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import StatusSelect from "@/components/StatusSelect";
import { useLiveRefresh } from "@/contexts/LiveRefresh";
import {
    STATUS_STYLES,
    MYTASK_STATUS_STYLES,
    MYTASK_STATUS_DOT,
    myTaskStatusOptions,
} from "@/components/StatusBadge";
import { ChevronLeft, ChevronRight, CalendarDays, Loader2 } from "lucide-react";
import { toast } from "sonner";

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function ymd(d) {
    return d.toISOString().slice(0, 10);
}

function buildGrid(year, month) {
    const first = new Date(Date.UTC(year, month - 1, 1));
    // ISO weekday: Mon=1..Sun=7; we want Mon-first grid
    const dayOfWeek = (first.getUTCDay() + 6) % 7; // 0..6, Mon=0
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const cells = [];
    // leading blanks
    for (let i = 0; i < dayOfWeek; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
        cells.push(ymd(new Date(Date.UTC(year, month - 1, d))));
    }
    // pad to multiple of 7
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
}

export default function Calendar() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const today = new Date();
    const [year, setYear] = useState(today.getUTCFullYear());
    const [month, setMonth] = useState(today.getUTCMonth() + 1);
    const [source, setSource] = useState("tasks"); // "tasks" | "my_tasks" (admin only)
    const [days, setDays] = useState({});
    const [loading, setLoading] = useState(true);
    const [selectedDay, setSelectedDay] = useState(null);
    const detailRef = useRef(null);

    const isMy = source === "my_tasks";

    const load = async (y, m, src) => {
        setLoading(true);
        try {
            const { data } = await api.get("/calendar", {
                params: { year: y, month: m, source: src },
            });
            setDays(data.days || {});
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(year, month, source); }, [year, month, source]);
    useLiveRefresh(() => load(year, month, source));

    useEffect(() => {
        if (selectedDay && detailRef.current) {
            detailRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [selectedDay]);

    const changeStatus = async (taskId, status) => {
        try {
            await api.patch(`/${isMy ? "my-tasks" : "tasks"}/${taskId}`, { status });
            load(year, month, source);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const grid = useMemo(() => buildGrid(year, month), [year, month]);
    const todayIso = ymd(new Date());

    const goPrev = () => {
        if (month === 1) {
            setMonth(12); setYear((y) => y - 1);
        } else setMonth((m) => m - 1);
        setSelectedDay(null);
    };
    const goNext = () => {
        if (month === 12) {
            setMonth(1); setYear((y) => y + 1);
        } else setMonth((m) => m + 1);
        setSelectedDay(null);
    };
    const goToday = () => {
        setYear(today.getUTCFullYear());
        setMonth(today.getUTCMonth() + 1);
        setSelectedDay(todayIso);
    };

    // Per-source presentation: My Tasks have their own statuses and a title.
    const chipStyles = isMy ? MYTASK_STATUS_STYLES : STATUS_STYLES;
    const primaryLabel = (t) => (isMy ? t.title : t.client_name) || "";

    const tasksForDay = selectedDay ? days[selectedDay] || [] : [];

    return (
        <div className="space-y-6" data-testid="calendar-page">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                        Scheduling
                    </div>
                    <h1 className="font-display text-4xl font-extrabold tracking-tight text-foreground sm:text-5xl">
                        Calendar
                    </h1>
                    <p className="mt-2 text-sm text-muted-foreground">
                        Engagements by deadline. Click any day to see the work due.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {isAdmin && (
                        <Select
                            value={source}
                            onValueChange={(v) => { setSource(v); setSelectedDay(null); }}
                        >
                            <SelectTrigger className="w-36" data-testid="cal-source-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="tasks">Tasks</SelectItem>
                                <SelectItem value="my_tasks">My Tasks</SelectItem>
                            </SelectContent>
                        </Select>
                    )}
                    <Button variant="outline" onClick={goPrev} data-testid="cal-prev">
                        <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                        <SelectTrigger className="w-36" data-testid="cal-month-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {MONTHS.map((m, i) => (
                                <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                        <SelectTrigger className="w-24" data-testid="cal-year-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {Array.from({ length: 7 }).map((_, i) => {
                                const y = today.getUTCFullYear() - 3 + i;
                                return (
                                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                                );
                            })}
                        </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={goNext} data-testid="cal-next">
                        <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button onClick={goToday} data-testid="cal-today">Today</Button>
                </div>
            </div>

            <Card className="sdc-card overflow-hidden">
                {loading ? (
                    <div className="grid place-items-center py-20">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-7 border-b border-border bg-secondary/40">
                            {WEEKDAYS.map((w) => (
                                <div
                                    key={w}
                                    className="px-3 py-2 text-[11px] uppercase tracking-[0.12em] text-muted-foreground"
                                >
                                    {w}
                                </div>
                            ))}
                        </div>
                        <div className="grid grid-cols-7">
                            {grid.map((iso, idx) => {
                                if (!iso) {
                                    return (
                                        <div
                                            key={`blank-${idx}`}
                                            className="min-h-[110px] border-b border-r border-border bg-secondary/10"
                                        />
                                    );
                                }
                                const list = days[iso] || [];
                                const isToday = iso === todayIso;
                                const isSelected = selectedDay === iso;
                                return (
                                    <button
                                        key={iso}
                                        onClick={() => setSelectedDay(iso)}
                                        className={`group min-h-[110px] border-b border-r border-border p-2 text-left transition-colors ${
                                            isSelected
                                                ? "bg-primary/5"
                                                : "bg-white hover:bg-secondary/40"
                                        }`}
                                        data-testid={`cal-day-${iso}`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span
                                                className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${
                                                    isToday
                                                        ? "bg-primary text-primary-foreground"
                                                        : "text-foreground"
                                                }`}
                                            >
                                                {Number(iso.slice(8, 10))}
                                            </span>
                                            {list.length > 0 && (
                                                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                                                    {list.length}
                                                </span>
                                            )}
                                        </div>
                                        <div className="mt-1 space-y-1">
                                            {list.slice(0, 3).map((t) => (
                                                <div
                                                    key={t.id}
                                                    className={`truncate rounded px-1.5 py-0.5 text-[10px] font-medium ${
                                                        chipStyles[t.status] ||
                                                        "bg-muted text-muted-foreground"
                                                    }`}
                                                >
                                                    {t.work_type}
                                                </div>
                                            ))}
                                            {list.length > 3 && (
                                                <div className="text-[10px] text-muted-foreground">
                                                    +{list.length - 3} more
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </>
                )}
            </Card>

            {selectedDay && (
                <Card ref={detailRef} className="sdc-card p-5" data-testid="cal-day-detail">
                    <div className="flex items-center gap-2">
                        <CalendarDays className="h-4 w-4 text-primary" />
                        <h2 className="font-display text-xl font-extrabold tracking-tight">
                            {new Date(selectedDay).toLocaleDateString("en-IN", {
                                weekday: "long",
                                day: "2-digit",
                                month: "long",
                                year: "numeric",
                            })}
                        </h2>
                    </div>
                    {tasksForDay.length === 0 ? (
                        <p className="mt-3 text-sm text-muted-foreground">
                            Nothing scheduled for this day.
                        </p>
                    ) : (
                        <ul className="mt-4 divide-y divide-border">
                            {tasksForDay.map((t) => (
                                <li
                                    key={t.id}
                                    className="flex items-center justify-between gap-3 py-2.5"
                                >
                                    <div className="flex flex-wrap items-center gap-x-2 text-sm font-semibold">
                                        {primaryLabel(t) && <span>{primaryLabel(t)}</span>}
                                        {primaryLabel(t) && t.work_type && (
                                            <span className="text-muted-foreground">·</span>
                                        )}
                                        <span>{t.work_type}</span>
                                    </div>
                                    <StatusSelect
                                        value={t.status}
                                        onChange={(v) => changeStatus(t.id, v)}
                                        options={
                                            isMy ? myTaskStatusOptions(t.category) : undefined
                                        }
                                        styles={isMy ? MYTASK_STATUS_STYLES : undefined}
                                        dot={isMy ? MYTASK_STATUS_DOT : undefined}
                                    />
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            )}
        </div>
    );
}
